import { HeroIcon } from '@components/HeroIcon'
import { Button } from '@components/Button'
import {
  GetProps,
  Label,
  Paragraph,
  Input as TamaguiInput,
  Text,
  XStack,
  YStack,
  styled,
} from 'tamagui'
import { Masks, formatWithMask } from 'react-native-mask-input'
import { StylableComponent } from '@tamagui/core'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useState } from 'react'

type InputStylesType<T extends StylableComponent> = GetProps<T>

const inputStyles: InputStylesType<typeof TamaguiInput> = {
  paddingHorizontal: '$size.spacing-xs',
  paddingVertical: '$size.spacing-xxxs',
  borderWidth: 1,
  borderColor: '$border-default',
  backgroundColor: '$bg-input',
  ff: '$mulishRegular',
  fontSize: '$font-size-xs',
  color: '$text-primary',
  br: '$border-radius-md',
  height: 48,
}

const BaseInput = styled(TamaguiInput, {
  name: 'Input',
  unstyled: true,
  ...inputStyles,
  placeholderTextColor: '$text-placeholder',
  focusStyle: {
    outlineWidth: 0,
    borderColor: '$border-focus',
  },
  variants: {
    error: {
      true: {
        borderColor: '$danger-300',
      },
    },
    hasIcon: {
      true: {
        paddingLeft: '$size.spacing-xl',
      },
    },
  } as const,
})

const BaseFakeInput = styled(Paragraph, {
  name: 'FakeInput',
  unstyled: true,
  ...(inputStyles as InputStylesType<typeof Paragraph>),
  height: 35,

  focusStyle: {
    borderColor: '$accent',
  },
  variants: {
    error: {
      true: {
        borderColor: '$danger-300',
      },
    },
  },
})

type ContainerProps = {
  children: React.ReactNode
  errorMessage?: string | false
  label?: string
  displayErrorIcon?: boolean
}

function Container({
  children,
  errorMessage,
  label,
  displayErrorIcon = true,
}: ContainerProps) {
  return (
    <YStack gap="$size.spacing-nano">
      <YStack gap="$size.spacing-xxxs">
        {label && (
          <Label
            color="$text-primary"
            ff="$mulishRegular"
            fontSize="$font-size-xxs"
            lineHeight={21}
          >
            {label}
          </Label>
        )}
        {children}
      </YStack>
      {!!errorMessage && (
        <XStack gap="$size.spacing-nano" alignItems="center">
          {displayErrorIcon && (
            <HeroIcon
              icon={['fal', 'exclamation-circle']}
              size={16}
              color="$danger-300"
            />
          )}
          <Text
            color="$danger-300"
            ff="$mulishRegular"
            fontSize="$font-size-xxxs"
          >
            {errorMessage}
          </Text>
        </XStack>
      )}
    </YStack>
  )
}

const DATE_MMYY = (text = '') => {
  const cleanText = text.replace(/\D+/g, '')

  const firstDigit = cleanText.charAt(0)
  let secondDigitMask = /\d/

  if (firstDigit === '0') {
    secondDigitMask = /[1-9]/
  } else if (firstDigit === '1') {
    secondDigitMask = /[0-2]/
  }

  return [/[0-1]/, secondDigitMask, '/', /\d/, /\d/]
}
const ALPHANUMERIC = /[a-zA-Z0-9]/

// Novo modelo de CPF aceita caracteres alfanuméricos (base de CPFs já contém alguns).
const CPF_ALPHANUMERIC = [
  ALPHANUMERIC,
  ALPHANUMERIC,
  ALPHANUMERIC,
  '.',
  ALPHANUMERIC,
  ALPHANUMERIC,
  ALPHANUMERIC,
  '.',
  ALPHANUMERIC,
  ALPHANUMERIC,
  ALPHANUMERIC,
  '-',
  ALPHANUMERIC,
  ALPHANUMERIC,
]

const BRL_RG = [
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /[0-9xX]/,
]

const ALL_MASKS = {
  ...Masks,
  DATE_MMYY,
  BRL_RG,
  CPF_ALPHANUMERIC,
}

export type InputProps = React.ComponentProps<typeof BaseInput> & {
  errorMessage?: string | false
  mask?: keyof typeof ALL_MASKS
  name?: string
  value?: string
  onChangeText?: (text: string) => void
  onClear?: () => void
  label?: string
  icon?: IconProp
  iconColor?: string
  displayErrorIcon?: boolean
}

// Área de toque dos botões de ação do input (limpar/mostrar senha). O ícone
// tem 16px; o alvo clicável precisa ser bem maior que isso.
const ACTION_BUTTON_SIZE = 44

// Botão de ação dentro do input: sem fundo (variant icon do Button padrão),
// mesmo tamanho e alinhamento para o limpar e o mostrar/ocultar senha.
function InputActionButton({
  onPress,
  icon,
  right,
  label,
  testID,
}: {
  onPress: () => void
  icon: IconProp
  right: number
  label: string
  testID: string
}) {
  return (
    <Button
      icon
      onPress={onPress}
      position="absolute"
      right={right}
      top={0}
      bottom={0}
      width={ACTION_BUTTON_SIZE}
      height="100%"
      minHeight={ACTION_BUTTON_SIZE}
      padding={0}
      zIndex={1}
      justifyContent="center"
      alignItems="center"
      aria-label={label}
      testID={testID}
    >
      <HeroIcon icon={icon} size={16} color="$text-placeholder" />
    </Button>
  )
}

export function Input({
  errorMessage,
  value,
  onChangeText,
  mask,
  label,
  icon,
  iconColor = '$text-placeholder',
  displayErrorIcon = true,
  secureTextEntry,
  onClear,
  ...rest
}: InputProps) {
  const { masked } = formatWithMask({
    text: value,
    mask: mask ? ALL_MASKS[mask] : undefined,
  })

  const [isSecureTextEntry, setIsSecureTextEntry] = useState(secureTextEntry)

  return (
    <Container
      errorMessage={errorMessage}
      label={label}
      displayErrorIcon={displayErrorIcon}
    >
      <XStack position="relative">
        {icon && (
          <YStack
            position="absolute"
            left="$size.spacing-xs"
            top={0}
            bottom={0}
            justifyContent="center"
            zIndex={1}
          >
            <HeroIcon icon={icon} size={16} color={iconColor} />
          </YStack>
        )}

        <BaseInput
          onChangeText={onChangeText}
          value={mask ? masked : value}
          error={!!errorMessage}
          secureTextEntry={isSecureTextEntry}
          hasIcon={!!icon}
          width="100%"
          {...rest}
        />

        {onClear && value && (
          <InputActionButton
            onPress={onClear}
            icon={['fal', 'times']}
            right={secureTextEntry ? ACTION_BUTTON_SIZE : 0}
            label="Limpar"
            testID="input-clear-button"
          />
        )}
        {secureTextEntry && (
          <InputActionButton
            onPress={() => setIsSecureTextEntry(!isSecureTextEntry)}
            icon={['fal', isSecureTextEntry ? 'eye' : 'eye-slash']}
            right={0}
            label={isSecureTextEntry ? 'Mostrar senha' : 'Ocultar senha'}
            testID="input-password-toggle"
          />
        )}
      </XStack>
    </Container>
  )
}

export function FakeInput({
  errorMessage,
  children,
  cse,
}: InputProps & { cse?: string }) {
  return (
    <Container errorMessage={errorMessage}>
      <BaseFakeInput data-cse={cse} error={!!errorMessage}>
        {children}
      </BaseFakeInput>
    </Container>
  )
}
